import to from 'await-catch'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../../../components/ui/button'
import { BackPageHeader } from '../../../../components/ui/header'
import { confirmAppointment } from '../../../../api'
import useIsMobile from '../../../../hooks/useIsMobile'
import { MOBILE_BOTTOM_NAV_HEIGHT } from '../../../../components/ui/layout/mobileLayout/styled'
import AppointmentSummaryCard from './AppointmentSummaryCard'
import { buildConfirmAppointmentParams } from './buildConfirmAppointmentParams'

const ConfirmMultipleAppointment = ({
	onBack,
	onEditTurn,
	onMultipleConfirmed,
	setAppointmentsBookingResult,
	cartTurns = [],
	clinics = [],
	patient,
	auth,
	showLoader,
	hideLoader,
	addFlashMessage
}) => {
	const isMobile = useIsMobile()
	const [isSubmitting, setIsSubmitting] = useState(false)

	const resolveClinicName = turn => {
		const clinic = clinics.find(
			item =>
				item.codigoEmpresa == turn?.clinica || item.value == turn?.clinica || item.id == turn?.clinica
		)

		return clinic?.nombre || clinic?.label || turn?.nombreClinica || turn?.clinica || '-'
	}

	const resolveErrorMessage = err => {
		if (typeof err?.response?.data === 'string' && err.response.data.trim()) {
			return err.response.data
		}

		if (
			typeof err?.response?.data === 'object' &&
			typeof err.response.data?.message === 'string' &&
			err.response.data.message.trim()
		) {
			return err.response.data.message
		}

		return 'Error al agendar el turno'
	}

	const handleConfirm = async () => {
		if (!cartTurns.length) {
			addFlashMessage('No hay turnos seleccionados para confirmar')
			return
		}

		if (isSubmitting) return

		setIsSubmitting(true)
		showLoader()

		try {
			const turnsResult = await Promise.all(
				cartTurns.map(async turn => {
					const params = buildConfirmAppointmentParams({ turn, patient, auth })
					const [err] = await to(confirmAppointment(params, { skipErrorMessage: true }))

					return {
						idTurno: turn.idTurno,
						hasError: !!err,
						errorMessage: err ? resolveErrorMessage(err) : ''
					}
				})
			)

			setAppointmentsBookingResult({ turns: turnsResult })

			if (onMultipleConfirmed) {
				const confirmedCount = turnsResult.filter(result => !result.hasError).length
				onMultipleConfirmed(confirmedCount)
			}
		} catch (error) {
			addFlashMessage('Ocurrió un error al confirmar los turnos')
		} finally {
			hideLoader()
			setIsSubmitting(false)
		}
	}

	return (
		<Container>
			<Content>
				<BackPageHeader onBack={onBack} showBack={!isMobile} title="Agendar turnos" />

				<ListSection>
					{cartTurns.map(turn => (
						<AppointmentSummaryCard
							key={turn.idTurno}
							turn={turn}
							clinicName={resolveClinicName(turn)}
							onEdit={onEditTurn}
							showEdit={true}
						/>
					))}
				</ListSection>
			</Content>

			<ActionBar>
				<Button
					label={'Confirmar turnos'}
					$variant="primary"
					size={isMobile ? 'mobile' : 'xlarge'}
					onClick={handleConfirm}
					disabled={cartTurns.length === 0 || isSubmitting}
					style={{ width: '100%' }}
				/>
			</ActionBar>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	min-height: 80vh;
	width: 80vw;
	padding: 20px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: 100%;
		padding: 16px 16px 96px 16px;
	}
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`

const ListSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;
	max-width: 560px;
`

const ActionBar = styled.div`
	width: 360px;
	margin-top: 24px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		position: fixed;
		left: 0;
		right: 0;
		bottom: ${MOBILE_BOTTOM_NAV_HEIGHT};
		z-index: 20;
		width: auto;
		margin-top: 0;
		padding: 12px 16px;
		background: ${props => props.theme.colors.brand.white};
	}
`

export default ConfirmMultipleAppointment
